import { Button, Divider, Form, Row, Select, Spin } from "antd";
import {
  downloadSalesReport,
  getAllDispensaries,
  getMovementsByEquipment,
  MovementsByEquipmentDetail,
  ResponseGetDispensary,
} from "api/generated";
import { FormItem } from "common/components/form/FormItem";
import ResponsiveCol from "common/components/form/ResponsiveCol";
import {
  DateFilterType,
  DateRangePicker,
} from "common/components/widgets/datePicker/DateRangePicker";
import { querySerializer } from "common/helpers/queryUtils";
import { useDateRange } from "context/DateRangeContext";
import DistributionPointSelected from "context/MyDistributionPoint";
import { usePaginationTable } from "hooks/commons/usePaginationTable";
import { useTableSelection } from "hooks/commons/useTableSelection";
import { useClient } from "hooks/useClient";
import { useCallback, useContext, useEffect, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { saveJsonToFile } from "routes/stockControl/components/jsonViewer";
import { COMPLEMENT_PARENT } from "routes/cfdiComplements/ComplementsScreen";
import { handleAxiosError } from "common/helpers/generalUtils";
import {
  EXPORT_REPORT_PERMISSION,
  getPagePermissions,
} from "security/permissions";
import useLoggedUser from "hooks/useLoggedUser";
import HoseBySaleTable from "./components/HoseBySaleTable";
import SummarySection, {
  SummaryItem,
} from "common/components/table/SummarySection";
import ComplementOptions from "common/components/complements/ComplementOptions";
import { arraysAreEqual } from "services/utils/formatterUtil";

import type { SelectProps } from 'antd';

function HoseBySaleScreen(props: RouteComponentProps) {
  const { client } = useClient();
  const { distributionPoint } = useContext(DistributionPointSelected);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingEquipmets, setIsLoadingEquipments] = useState(false);
  const { permissions } = useLoggedUser();
  const exportReportsPermissions = getPagePermissions(
    EXPORT_REPORT_PERMISSION,
    permissions
  );
  const [dispensaries, setDispensaries] = useState<ResponseGetDispensary[]>();

  const [summary, setSummary] = useState<SummaryItem[]>([]);

  const params = new URLSearchParams(props.location.search);
  const eqParam = params.get("equipmentKeys");
  const initialPage = params.get("page");
  const [equipmentKeys, setEquipmentKeys] = useState<string[]>(
    eqParam ? eqParam.split(",") : []
  );

  const selection = useTableSelection();
  const { rangeTimeValue, setRangeTimeValue, getFormattedDates } =
    useDateRange();
  const { startDate, endDate } = getFormattedDates();
  const pagination = usePaginationTable<MovementsByEquipmentDetail>(props, {
    initialSort: ["folio,DESC"],
    initialPage: initialPage ? Number(initialPage) : undefined,
    persistInUrl: true,
  });

  const [parentIdToDelete, setParentIdToDelete] = useState<string>();

  const hosesToSelect: SelectProps['options'] = dispensaries?.map((eq) => ({
    label: eq.dispensaryCode,
    value: eq.dispensaryCode,
  })) ?? [];

  function downloadReport() {
    if (distributionPoint) {
      setIsLoading(true);
      downloadSalesReport({
        client,
        body: {
          startDate,
          endDate,
          distributionPointId: distributionPoint?.id,
          equipmentKeys,
        },
        path: {
          type: "csv",
        },
      })
        .then(({ data }) => {
          saveJsonToFile(data, "text/csv", "ventas.csv");
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }

  useEffect(() => {
    if (!distributionPoint) {
      return;
    }

    setIsLoadingEquipments(true);
    pagination.globalReset();
    setDispensaries(undefined);
    getAllDispensaries({
      client,
      path: {
        distributionPointId: distributionPoint?.id,
      },
    })
      .then(({ data }) => {
        const result = data ?? [];
        setDispensaries(result);
        // --- VALIDACIÓN DE KEYS EXISTENTES ---
        // Es posible que al cambiar de PDD las keys ya no sean validas
        // Esto limpiara elas keys que no coprrespondan al PDD elegido
        const validKeys = new Set(
          result.flatMap((d) => d.dispensaryCode ?? [])
        );
        const filtered = equipmentKeys.filter((key) => validKeys.has(key));
        if (!arraysAreEqual(filtered, equipmentKeys)) {
          setEquipmentKeys(filtered);
        }

        // -- SELECCION AUTOMATICA DE HOSE
        // Para el caso de que el punto de distribución elegido cuente con
        // solamente una manguera, esta sera seleccionada de manera automatica
        if (filtered.length === 0 && validKeys.size === 1) {
          const [first] = validKeys;
          if (first) setEquipmentKeys([first]);
        }
      })
      .catch((err) =>
        handleAxiosError(err, "No fue posible obtener dispensarios")
      )
      .finally(() => {
        setIsLoadingEquipments(false);
      });
  }, [client, distributionPoint]);

  const reloadTable = useCallback(() => {
    if (!distributionPoint || !dispensaries) {
      return;
    }
    if (equipmentKeys.length === 0) {
      pagination.globalReset();
      setSummary([]);
      return;
    }
    setIsLoading(true);
    getMovementsByEquipment({
      client,
      querySerializer: querySerializer,
      query: {
        pageable: {
          page: pagination.currentPage,
          size: pagination.pageSize,
          sort: pagination.sort,
        },
      },
      body: {
        startDate,
        endDate,
        displayPage: pagination.currentPage,
        displayAmount: pagination.pageSize,
        equipmentKeys: equipmentKeys,
        distributionPointId: distributionPoint.id,
      },
    })
      .then(({ data }) => {
        pagination.setTotalItems(data?.movementDetail?.totalElements ?? 0);
        pagination.setItems(data?.movementDetail?.content ?? []);

        setSummary([
          { label: "Litros vendidos", child: data?.totalVolume ?? 0 },
          {
            label: "Total venta",
            child: data?.totalPrice ?? 0,
            prefix: "$",
          },
        ]);
      })
      .catch((err) => handleAxiosError(err, "No fue posible obtener ventas"))
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    client,
    startDate,
    endDate,
    pagination.currentPage,
    pagination.pageSize,
    pagination.sort,
    equipmentKeys,
    dispensaries,
  ]);

  // useEffect principal
  useEffect(() => {
    reloadTable();
  }, [reloadTable]);

  // Reset cuando cambian filtros principales
  useEffect(() => {
    if (props.location.search && props.location.search.length > 0) {
      return;
    }
  }, [distributionPoint?.id, startDate, endDate]);

  useEffect(() => {
    selection.resetSelected();
  }, [distributionPoint?.id, startDate, endDate, equipmentKeys]);

  useEffect(() => {
    const locationParams = new URLSearchParams(props.location.search);

    if (equipmentKeys.length === 0) {
      locationParams.delete("equipmentKeys");
      locationParams.delete("page");
    } else {
      locationParams.set("equipmentKeys", equipmentKeys.join(","));
    }

    const newSearch = locationParams.toString();
    const currentSearch = props.location.search.replace(/^\?/, "");
    if (newSearch !== currentSearch) {
      props.history.replace({
        ...props.location,
        search: newSearch,
      });
    }
  }, [equipmentKeys]);

  return (
    <Spin
      size="large"
      tip="Cargando..."
      spinning={isLoading || isLoadingEquipmets}
    >
      <Form layout="vertical">
        <Row gutter={24} align="bottom">
          <ResponsiveCol>
            <FormItem label="Seleccionar equipos" required>
              <Select
                mode="multiple"
                placeholder="Selecciona uno o más equipos"
                style={{ width: "100%" }}
                options={hosesToSelect}
                value={equipmentKeys}
                onChange={setEquipmentKeys}
                loading={dispensaries === undefined}
                disabled={dispensaries === undefined}
              />
            </FormItem>
          </ResponsiveCol>

          <ResponsiveCol>
            <FormItem label="Seleccionar periodo">
              <DateRangePicker
                config={{
                  allowedTypes: [
                    DateFilterType.DAY,
                    DateFilterType.MONTH,
                    DateFilterType.RANGE,
                  ],
                  defaultType: DateFilterType.MONTH,
                  showQuickFilters: true,
                }}
                rangeTimeValue={rangeTimeValue}
                setRangeTimeValue={setRangeTimeValue}
              />
            </FormItem>
          </ResponsiveCol>

          <ResponsiveCol>
            <FormItem>
              <Button
                style={{ width: "100%" }}
                onClick={downloadReport}
                disabled={
                  !exportReportsPermissions || pagination.totalItems === 0
                }
              >
                Descargar CSV
              </Button>
            </FormItem>
          </ResponsiveCol>
        </Row>
      </Form>
      <ResponsiveCol size="xl">
        <SummarySection
          title="TOTAL"
          items={summary}
          isLoading={isLoading || isLoadingEquipmets}
        />
      </ResponsiveCol>

      <ComplementOptions
        selection={selection}
        reloadTable={reloadTable}
        parentType={COMPLEMENT_PARENT.VENTA}
        parentIdToDelete={parentIdToDelete}
        clearParentIdToDelete={() => {
          setParentIdToDelete(undefined);
        }}
      />

      <HoseBySaleTable
        selection={selection}
        isLoading={isLoading}
        reloadTable={reloadTable}
        pagination={pagination}
        deleteComplement={(record) => {
          setParentIdToDelete(record.idMovement);
        }}
        location={props.location}
        history={props.history}
        match={props.match}
      />
      <Divider />
    </Spin>
  );
}

export default withRouter(HoseBySaleScreen);
